// EchoesOverview —「回 聲」: who has uploaded a post-event voice reflection
// and who hasn't, across the 14 learners. Read-only — there is no download
// UI here; the worker downloads the .m4a files straight from the Firebase
// Console (Storage → echoes/{userId}/). This page just tells them who to
// chase on LINE.
//
// echoes is read directly (not via useAdminSync) — it's a one-off post-event
// view, no need to widen the live sync payload.

import {
  Hairline,
  Han,
  HUD,
  MiniPlayer,
  type MiniPlayerHandle,
  MonoData,
  MonoLabel,
  T,
  TextBtn,
} from "@goodbye/ui";
import {
  type EchoDoc,
  echoesCol,
  storageUrl,
  useFirestoreCollection,
  type WithId,
} from "@goodbye/firebase";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAdminSyncCtx } from "../context";
import { useEventConfig } from "../hooks/useEventConfig";
import { usePersistentState } from "../hooks/usePersistentState";
import { formatEchoMinutes } from "../lib/echoDuration";

function fmtWhen(ms: number | null): string {
  if (ms == null) return "";
  const d = new Date(ms);
  const md = `${d.getMonth() + 1}/${d.getDate()}`;
  const hm = `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes(),
  ).padStart(2, "0")}`;
  return `${md} ${hm}`;
}

export function EchoesOverview() {
  const { users } = useAdminSyncCtx();
  const { data: echoes } = useFirestoreCollection(echoesCol());
  const { cohortId: currentCohortId } = useEventConfig();

  const [cohortFilter, setCohortFilter] = usePersistentState<string>(
    "admin-cohortFilter",
    "all",
  );

  const availableCohorts = useMemo(() => {
    const ids = new Set<string>();
    for (const u of users) {
      if (u.role !== "worker") ids.add(u.cohortId ?? "2025");
    }
    return Array.from(ids).sort();
  }, [users]);

  // Learners only, filtered by selected cohort.
  const learners = useMemo(
    () =>
      [...users]
        .filter(
          (u) =>
            (u.role ?? "learner") === "learner" &&
            (cohortFilter === "all" || (u.cohortId ?? "2025") === cohortFilter),
        )
        .sort((a, b) => (a.no ?? "").localeCompare(b.no ?? "")),
    [users, cohortFilter],
  );

  // Group echoes by userId → the user's segments (newest first) + latest ms.
  const byUser = useMemo(() => {
    const m = new Map<
      string,
      { items: WithId<EchoDoc>[]; latest: number | null }
    >();
    for (const e of echoes) {
      const prev = m.get(e.userId) ?? { items: [], latest: null };
      const ms = e.createdAt?.toMillis?.() ?? null;
      prev.items.push(e);
      m.set(e.userId, {
        items: prev.items,
        latest:
          ms != null && (prev.latest == null || ms > prev.latest)
            ? ms
            : prev.latest,
      });
    }
    // Newest segment first within each user.
    for (const rec of m.values()) {
      rec.items.sort(
        (a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0),
      );
    }
    return m;
  }, [echoes]);

  const submittedCount = learners.filter((u) => byUser.has(u.id)).length;

  // Every submitted learner stays expanded. Only the active segment opens its
  // transport; tapping its row starts playback immediately.
  const [playingId, setPlayingId] = useState<string | null>(null);
  const playerRefs = useRef<Record<string, MiniPlayerHandle | null>>({});
  const [urlCache, setUrlCache] = useState<Record<string, string>>({});
  const [urlError, setUrlError] = useState<Record<string, boolean>>({});

  // When the URL for the currently-selected segment becomes available, start
  // playback. Handles the case where playSegment() was called before the URL
  // had resolved (fetchUrl path).
  useEffect(() => {
    if (!playingId || !urlCache[playingId]) return;
    void playerRefs.current[playingId]?.play();
  }, [urlCache, playingId]);

  // Resolve the small set of submitted files up front. This keeps the later
  // play action inside the worker's tap, which matters for mobile autoplay
  // policies, while the audio bytes themselves remain unloaded until needed.
  useEffect(() => {
    let cancelled = false;
    for (const echo of echoes) {
      storageUrl(echo.storagePath)
        .then((url) => {
          if (!cancelled) setUrlCache((cache) => ({ ...cache, [echo.id]: url }));
        })
        .catch(() => {
          if (!cancelled) setUrlError((errors) => ({ ...errors, [echo.id]: true }));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [echoes]);

  function fetchUrl(echoId: string, storagePath: string) {
    setUrlError((m) => ({ ...m, [echoId]: false }));
    storageUrl(storagePath)
      .then((u) => setUrlCache((c) => ({ ...c, [echoId]: u })))
      .catch(() => setUrlError((m) => ({ ...m, [echoId]: true })));
  }

  function playSegment(echoId: string, storagePath: string) {
    if (playingId && playingId !== echoId) playerRefs.current[playingId]?.pause();
    setPlayingId(echoId);
    if (urlCache[echoId] === undefined) {
      fetchUrl(echoId, storagePath);
      return;
    }
    void playerRefs.current[echoId]?.play();
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: T.paper,
      }}
    >
      <HUD label="工 作 端" />

      <div
        style={{
          padding: "20px 32px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <Han size={20} color={T.ink900}>
          迴 響
        </Han>
        <Han size={13} color={T.ink500}>
          已 交 {submittedCount} / {learners.length} 位
        </Han>
        <MonoLabel size={11} color={T.ink350}>
          檔案在 Console → Storage → echoes/{"{userId}"}/
        </MonoLabel>
        {availableCohorts.length > 1 && (
          <div style={{ display: "flex", gap: 0, marginTop: 4 }}>
            {[{ id: "all", label: "全 部" }, ...availableCohorts.map((c) => ({ id: c, label: `${c} 屆` }))].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setCohortFilter(id)}
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: `${cohortFilter === id ? 2 : 1}px solid ${cohortFilter === id ? T.ink900 : T.ink200}`,
                  padding: "12px 0",
                  marginRight: 20,
                  cursor: "pointer",
                  fontFamily: T.han,
                  fontSize: 13,
                  color: cohortFilter === id ? T.ink900 : T.ink500,
                  minHeight: 44,
                }}
              >
                {label}
                {id !== "all" && id === currentCohortId && (
                  <span style={{ color: T.ink500, fontSize: 11, marginLeft: 4 }}>· 本屆</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ padding: "12px 32px calc(120px + env(safe-area-inset-bottom, 0px))" }}>
        <Hairline style={{ marginBottom: 4 }} />
        {learners.map((u) => {
          const rec = byUser.get(u.id);
          const submitted = !!rec;
          const totalDuration = rec
            ? formatEchoMinutes(rec.items.map((e) => e.duration))
            : "";
          return (
            <div key={u.id}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "14px 0",
                  cursor: "default",
                }}
              >
                <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                  <MonoLabel size={11} color={T.ink350}>
                    {u.no}
                  </MonoLabel>
                  <Han size={14} color={submitted ? T.ink900 : T.ink350}>
                    {u.name}
                  </Han>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                  {submitted ? (
                    <>
                      {rec.latest != null && (
                        <MonoData size={11} color={T.ink350}>
                          {fmtWhen(rec.latest)}
                        </MonoData>
                      )}
                      {totalDuration && (
                        <MonoData size={11} color={T.ink500}>
                          · {totalDuration}
                        </MonoData>
                      )}
                      <Han size={12} color={T.ink900} ls="0.12em">
                        · {rec.items.length} 段
                      </Han>
                    </>
                  ) : (
                    <Han size={12} color={T.ink350} ls="0.12em">
                      · 未 交
                    </Han>
                  )}
                </div>
              </div>

              {/* Submitted learners are always expanded. */}
              {submitted && (
                <div style={{ paddingBottom: 12 }}>
                  {rec.items.map((e, i) => {
                    const segOpen = playingId === e.id;
                    return (
                      <div key={e.id} style={{ paddingLeft: 22 }}>
                        <div
                          onClick={() => playSegment(e.id, e.storagePath)}
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            gap: 12,
                            padding: "10px 0",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0 }}>
                            <MonoLabel size={11} color={T.ink350}>
                              {String(rec.items.length - i).padStart(2, "0")}
                            </MonoLabel>
                            <MonoData size={11} color={T.ink350}>
                              {fmtWhen(e.createdAt?.toMillis?.() ?? null)}
                            </MonoData>
                          </div>
                          <Han size={12} color={segOpen ? T.ink900 : T.ink500} ls="0.16em">
                            {segOpen ? "· 播 放 中" : "· 播 放"}
                          </Han>
                        </div>
                        <div
                          style={{
                            display: segOpen ? "block" : "none",
                            paddingBottom: segOpen ? 10 : 0,
                          }}
                        >
                            {urlError[e.id] ? (
                              <div style={{ minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <TextBtn onClick={() => fetchUrl(e.id, e.storagePath)} color={T.ink700}>
                                  · 載 入 失 敗，點 此 重 試
                                </TextBtn>
                              </div>
                            ) : (
                              <MiniPlayer
                                ref={(player) => {
                                  playerRefs.current[e.id] = player;
                                }}
                                src={urlCache[e.id] ?? ""}
                                fallbackDuration={e.duration}
                                loading={urlCache[e.id] === undefined}
                              />
                            )}
                        </div>
                        <Hairline />
                      </div>
                    );
                  })}
                </div>
              )}

              <Hairline />
            </div>
          );
        })}
      </div>
    </div>
  );
}
